import { printerMode } from "@/lib/printer-mode";
import { getActiveStore } from "@/lib/scope";
import ApiImage from "@/components/ApiImage";
import WriteButton from "@/components/WriteButton";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api, apiError } from "@/lib/pos-api";
import { rupiah, imgUrl } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Search, Plus, Minus, Trash2, ShoppingCart, X, PackageX, CreditCard, RotateCcw, Pause, Wifi, WifiOff, RefreshCw, StickyNote, User, UserPlus, Clock, AlertTriangle, PanelLeftOpen, PanelLeftClose, Keyboard, Hand, Lock, LockOpen, Download,
} from "lucide-react";
import PaymentModal from "@/components/PaymentModal";
import ReceiptModal from "@/components/ReceiptModal";
import { printReceipt } from "@/lib/printReceipt";
import { printViaBluetooth } from "@/lib/btprint";
import PreviousDebtDialog from "@/components/PreviousDebtDialog";
import CustomerDialog from "@/components/CustomerDialog";
import ShiftCloseModal from "@/components/ShiftCloseModal";
import CartActions from "@/components/CartActions";
import MoneyInput from "@/components/MoneyInput";
import { productCodeMatches, productMatchesSearch } from "@/lib/product-codes.mjs";
import { appendCartItem, changeVariantQuantity, itemVariantQuantities, variantSummary, variantTotal } from "@/lib/variant-quantities.mjs";
import { getQueue, enqueueTx, syncQueue, buildLocalTx, cacheCatalog, cacheProductsOnly, readCachedProducts, readCachedCategories } from "@/lib/offline";

export default function POS() {
  const { user, readOnly } = useAuth();
  const { navHidden, setNavHidden, posDraft, setPosDraft } = useOutletContext();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");
  const cart = posDraft.cart;
  const setCart = useCallback((value) => setPosDraft((previous) => ({ ...previous, cart: typeof value === "function" ? value(previous.cart) : value })), [setPosDraft]);
  const [expandedCart, setExpandedCart] = useState(null);
  const [cartHi, setCartHi] = useState(-1);
  const [hi, setHi] = useState(-1);
  const gridRef = useRef(null);
  const cartRef = useRef(null);
  const [touchMode, setTouchMode] = useState(() => {
    const v = localStorage.getItem("pos_touch");
    if (v !== null) return v === "1";
    return typeof window !== "undefined" && (navigator.maxTouchPoints > 0 || "ontouchstart" in window);
  });
  const toggleTouch = () => setTouchMode((v) => { const n = !v; localStorage.setItem("pos_touch", n ? "1" : "0"); return n; });
  const discount = posDraft.discount;
  const setDiscount = useCallback((value) => setPosDraft((previous) => ({ ...previous, discount: typeof value === "function" ? value(previous.discount) : value })), [setPosDraft]);
  const [holds, setHolds] = useState([]);
  const customer = posDraft.customer;
  const setCustomer = useCallback((value) => setPosDraft((previous) => ({ ...previous, customer: typeof value === "function" ? value(previous.customer) : value, previousDebts: [] })), [setPosDraft]);
  const previousDebts = posDraft.previousDebts || [];
  const [debtCustomer, setDebtCustomer] = useState(null);
  const resumePayment = useRef(false);
  const [payBusy, setPayBusy] = useState(false);
  const paying = useRef(false);
  const paymentAttempt = useRef(null);
  const finishDebtChoice = useCallback((items) => {
    setPosDraft(previous => ({ ...previous, previousDebts: items }));
    setDebtCustomer(null);
    if (resumePayment.current) { setPayOpen(true); resumePayment.current = false; }
  }, [setPosDraft]);
  const selectCustomer = (value) => {
    if (paying.current) return;
    setCustomer(value);
    resumePayment.current = payOpen;
    if (value) { setPayOpen(false); setDebtCustomer(value); }
  };
  const receiptNote = posDraft.note || "";
  const setReceiptNote = (note) => setPosDraft(previous => ({ ...previous, note }));
  const [cartAction, setCartAction] = useState(null);
  const [custOpen, setCustOpen] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [holdOpen, setHoldOpen] = useState(false);
  const [holdLabel, setHoldLabel] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const [shift, setShift] = useState(null);
  const [shiftLoaded, setShiftLoaded] = useState(false);
  const [shiftCloseOpen, setShiftCloseOpen] = useState(false);
  const [storeName, setStoreName] = useState("");
  const [priceProd, setPriceProd] = useState(null);
  const [priceVal, setPriceVal] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(getQueue().length);
  const [fs, setFs] = useState(false);
  const [installEvt, setInstallEvt] = useState(null);
  const searchRef = useRef(null);
  const qtyRef = useRef(null);
  const optsRef = useRef(null);
  const [quick, setQuick] = useState(null); // { product }
  const [quickSel, setQuickSel] = useState(0); // index into [base, ...units]
  const [quickQty, setQuickQty] = useState("1");
  const [quickPrice, setQuickPrice] = useState(""); // "" = harga default
  const [showPriceEdit, setShowPriceEdit] = useState(false);
  const [quickVariantCounts, setQuickVariantCounts] = useState([]);
  const [quickVarSel, setQuickVarSel] = useState(0);
  const [quickVariantFocused, setQuickVariantFocused] = useState(false);
  const [quickDiscount, setQuickDiscount] = useState("");
  const [quickNote, setQuickNote] = useState("");
  const varRef = useRef(null);
  const discRef = useRef(null);
  const noteRef = useRef(null);
  const quickHasVariants = !!quick?.product?.variants?.length;
  const quickVariantQuantities = (quick?.product?.variants || []).map((variant, index) => ({ name: variant.name, qty: quickVariantCounts[index] || 0 })).filter((value) => value.qty > 0);
  const quickHasSelectedVariants = quickVariantQuantities.length > 0;
  const quickTotalQty = quickHasSelectedVariants ? variantTotal(quickVariantQuantities) : Number(quickQty) || 0;

  const load = async () => {
    try {
      const [p, c] = await Promise.all([api.get("/products"), api.get("/categories")]);
      setProducts(p.data);
      setCategories(c.data);
      cacheCatalog(p.data, c.data);
    } catch (e) {
      const cp = readCachedProducts();
      if (cp) {
        setProducts(cp);
        setCategories(readCachedCategories() || []);
        toast.warning("Mode offline: memakai data katalog tersimpan");
      } else {
        toast.error(apiError(e));
      }
    }
  };

  const loadHolds = async () => {
    try { const { data } = await api.get("/holds"); setHolds(data); } catch { /* offline */ }
  };

  const loadShift = async () => {
    try {
      const { data } = await api.get("/shifts/active");
      setShift(data);
    } catch { /* offline: biarkan state lama */ }
    finally { setShiftLoaded(true); }
  };

  const openShift = async () => {
    if (readOnly) return;
    try {
      const { data } = await api.post("/shifts/open");
      setShift(data);
      toast.success("Shift dibuka. Selamat berjualan!");
    } catch (e) { toast.error(apiError(e)); }
  };

  const doSync = async () => {
    if (readOnly) return;
    if (!getQueue().length) return;
    const { ok, fail } = await syncQueue(api);
    setPending(getQueue().length);
    if (ok) { toast.success(`${ok} transaksi offline tersinkron`); load(); }
    if (fail) toast.error(`${fail} transaksi belum tersinkron, coba lagi`);
  };

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setInstallEvt(e); };
    const onInstalled = () => setInstallEvt(null);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  const doInstall = async () => {
    if (!installEvt) { toast.info("Di browser: buka menu ⋮ lalu pilih 'Instal aplikasi' / 'Tambah ke Layar Utama'"); return; }
    installEvt.prompt();
    try { await installEvt.userChoice; } catch { /* ignore */ }
    setInstallEvt(null);
  };

  const toggleFull = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        document.body.classList.add("pos-kiosk");
        setFs(true);
      } else {
        await document.exitFullscreen();
        document.body.classList.remove("pos-kiosk");
        setFs(false);
      }
    } catch {
      // Fallback tanpa Fullscreen API: cukup sembunyikan tampilan lain
      const on = document.body.classList.toggle("pos-kiosk");
      setFs(on);
    }
  };

  useEffect(() => {
    const onFsChange = () => {
      const active = !!document.fullscreenElement;
      setFs(active);
      if (!active) document.body.classList.remove("pos-kiosk");
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => { document.removeEventListener("fullscreenchange", onFsChange); document.body.classList.remove("pos-kiosk"); };
  }, []);

  useEffect(() => {
    searchRef.current?.focus();
    load();
    loadHolds();
    loadShift();
    api.get("/stores").then((r) => {
      const sid = getActiveStore();
      const st = r.data.find((s) => s.id === sid) || r.data.find((s) => s.is_default) || r.data[0];
      if (st) setStoreName(st.name);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const goOnline = () => { setOnline(true); doSync(); };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    if (navigator.onLine) doSync();
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
    // eslint-disable-next-line
  }, []);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (!p.active) return false;
      if (activeCat !== "all" && p.category_id !== activeCat) return false;
      return productMatchesSearch(p, search);
    });
  }, [products, activeCat, search]);

  const autoTierIndex = (tiers, qty) => {
    if (!tiers || tiers.length === 0) return 0;
    let best = -1, bestMin = -1;
    tiers.forEach((t, i) => {
      const mq = Number(t.min_qty || 1);
      if (qty >= mq && mq > bestMin) { bestMin = mq; best = i; }
    });
    if (best === -1) {
      let lowest = 0, lowestMin = Infinity;
      tiers.forEach((t, i) => {
        const mq = Number(t.min_qty || 1);
        if (mq < lowestMin) { lowestMin = mq; lowest = i; }
      });
      best = lowest;
    }
    return best;
  };

  // Harga grosir per satuan: tier dengan NAMA sama dgn satuan dipakai bila qty >= min_qty.
  const tierPriceFor = (tiers, unitName, qty) => {
    const name = (unitName || "").trim().toLowerCase();
    let best = null;
    (tiers || []).forEach((t) => {
      if ((t.name || "").trim().toLowerCase() !== name) return;
      if (Number(qty) >= Number(t.min_qty || 1) && (!best || Number(t.min_qty || 1) > Number(best.min_qty || 1))) best = t;
    });
    return best ? Number(best.price || 0) : null;
  };
  const baseSellable = (p) => {
    const tiers = p.price_tiers || [];
    const hasUnits = (p.units || []).length > 0;
    if (!hasUnits) return tiers.some((t) => Number(t.price) > 0);
    const baseName = (p.base_unit || "Pcs").trim().toLowerCase();
    return tiers.some((t) => (t.name || "").trim().toLowerCase() === baseName && Number(t.price) > 0);
  };

  const tierOf = (it) => {
    const idx = it.manualTier != null ? it.manualTier : autoTierIndex(it.tiers, it.qty);
    return { idx, tier: (it.tiers && it.tiers[idx]) || { name: "-", price: 0 } };
  };

  const setManualTier = (i, idx) =>
    setCart((prev) => prev.map((it, x) => (x === i ? { ...it, manualTier: idx } : it)));

  const setCartUnit = (i, unit) =>
    setCart((prev) => prev.map((it, x) => (x === i ? { ...it, unit } : it)));
  const priceOf = (it) => {
    if (it.manualPrice != null) return it.manualPrice;
    const hasUnits = (it.units || []).length > 0;
    if (it.unit) {
      const tp = tierPriceFor(it.tiers, it.unit.name, it.qty);
      return (tp != null ? tp : Number(it.unit.price || 0)) || 0;
    }
    const baseName = it.baseUnit || "Pcs";
    const tpBase = tierPriceFor(it.tiers, baseName, it.qty);
    if (tpBase != null) return tpBase;
    if (!hasUnits) return tierOf(it).tier.price || 0;
    return 0;
  };
  const piecesOf = (it) => (it.unit ? Number(it.unit.pieces || 1) : 1);
  const isBelowCost = (it) => {
    const effectivePrice = priceOf(it) - (it.qty ? Number(it.discount || 0) / it.qty : 0);
    return (it.cost || 0) > 0 && effectivePrice < (it.cost || 0) * piecesOf(it) - 0.001;
  };
  const holdTotal = (q) => {
    const items = q.payload?.items || [];
    const sub = items.reduce((s, it) => s + priceOf(it) * (Number(it.qty) || 0) - (Number(it.discount) || 0), 0);
    return Math.max(0, sub - (Number(q.payload?.discount) || 0)) + (q.payload?.previousDebts || []).reduce((sum, debt) => sum + debt.amount, 0);
  };
  const labelOf = (it) => (it.unit ? it.unit.name : tierOf(it).tier.name);

  const addToCart = (p, unit = null, qty = 1, priceOverride = null, variant = null, itemDiscount = 0, itemNote = "", variantQuantities = []) => {
    if (readOnly) return;
    const tiers = (p.price_tiers && p.price_tiers.length) ? p.price_tiers : [{ name: "Umum", min_qty: 1, price: 0 }];
    const addQty = Number(qty) > 0 ? Number(qty) : 1;
    const mp = priceOverride != null && priceOverride !== "" ? Number(priceOverride) : null;
    const disc = Math.max(0, Number(itemDiscount) || 0);
    const note = (itemNote || "").trim();
    setCart((prev) => appendCartItem(prev, { product_id: p.id, name: p.name, variant, variant_quantities: variantQuantities, qty: addQty, tiers, manualTier: null, manualPrice: mp, cost: p.cost || 0, stock: p.stock, units: p.units || [], baseUnit: p.base_unit || "Pcs", unit, discount: disc, note }));
  };

  // Auto-scroll keranjang ke item terbaru (paling bawah) saat ada item ditambah
  const cartQtyTotal = cart.reduce((s, it) => s + it.qty, 0);
  useEffect(() => {
    const el = cartRef.current;
    if (el) requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  }, [cart.length, cartQtyTotal]);

  // Opsi satuan yang muncul: satuan dasar (Pcs) hanya ikut bila harganya sudah diisi,
  // selain itu hanya satuan yang diatur di produk (Dus/Slop/dll).
  const optsFor = (p) => {
    const arr = [...(baseSellable(p) ? [null] : []), ...(p.units || [])];
    return arr.length ? arr : [null];
  };
  const quickOpts = quick ? optsFor(quick.product) : [];
  const productUnitPrice = (p, u, qty) => {
    const tiers = p.price_tiers || [];
    const unitName = u ? u.name : (p.base_unit || "Pcs");
    const tp = tierPriceFor(tiers, unitName, qty);
    if (tp != null) return tp;
    if (u) return Number(u.price || 0);
    if (!(p.units || []).length) return tiers[autoTierIndex(tiers, Number(qty) || 1)]?.price || 0;
    return 0;
  };
  const quickUnitPrice = (u, qty) => productUnitPrice(quick.product, u, qty);

  // Klik produk: jika punya satuan lebih dari 1 (Pcs + Slop/Dus) buka popup pilih satuan
  // (bisa navigasi panah ↑/↓ lalu Enter), lalu isi QTY & Enter untuk masuk keranjang.
  const openQuick = (p, preUnit = undefined) => {
    const matches = productCodeMatches(p, search);
    if (matches.length > 1) {
      toast.error("SKU / Barcode digunakan lebih dari sekali. Periksa kode satuan dan varian produk.");
      return;
    }
    const match = matches[0];
    if (preUnit === undefined && match?.unit) preUnit = match.unit;
    const opts = optsFor(p);
    let idx = 0;
    if (preUnit !== undefined) {
      idx = Math.max(0, opts.findIndex((u) => (u?.name || null) === (preUnit?.name || null)));
    }
    setQuick({ product: p });
    setQuickSel(idx);
    setQuickQty("1");
    setQuickPrice("");
    setShowPriceEdit(false);
    const variantIndex = match?.variant ? p.variants.indexOf(match.variant) : 0;
    setQuickVarSel(variantIndex);
    setQuickVariantFocused(false);
    setQuickVariantCounts((p.variants || []).map(() => 0));
    setQuickDiscount("");
    setQuickNote("");
    const hasUnits = (p.units || []).length > 0;
    setTimeout(() => {
      (hasUnits ? optsRef.current : qtyRef.current)?.focus();
      if (!hasUnits) qtyRef.current?.select();
    }, 60);
  };

  const onSearchKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (filtered.length) { setHi(0); gridRef.current?.focus(); }
    } else if (e.key === "Enter" && search.trim()) {
      e.preventDefault();
      const matches = products.filter((p) => p.active).flatMap((p) => productCodeMatches(p, search));
      if (matches.length > 1) {
        toast.error("SKU / Barcode digunakan lebih dari sekali. Pilih produk atau perbaiki kodenya.");
      } else if (matches.length === 1) {
        if (readOnly || e.repeat) return;
        const { product: p, unit: scannedUnit, variant: scannedVariant } = matches[0];
        const unit = scannedUnit || optsFor(p)[0] || null;
        const variant = scannedVariant?.name || null;
        addToCart(p, unit, 1, null, variant);
        setSearch("");
        searchRef.current?.focus();
      } else if (filtered.length === 1) {
        openQuick(filtered[0]);
      }
    }
  };

  const gotoQty = () => setTimeout(() => { qtyRef.current?.focus(); qtyRef.current?.select(); }, 20);
  const changeQuickVariant = (index, delta = 1) => {
    setQuickVarSel(index);
    setQuickVariantCounts((previous) => previous.map((qty, i) => i === index ? Math.max(0, qty + delta) : qty));
  };

  const openPrice = (p) => {
    if (user.role !== "admin") { toast.error("Hanya admin yang dapat mengatur harga"); return; }
    const tiers = p.price_tiers || [];
    setPriceProd(p);
    setPriceVal(tiers[0]?.price ? String(tiers[0].price) : "");
  };

  const savePrice = async () => {
    if (readOnly) return;
    const val = Number(priceVal || 0);
    if (!(val > 0)) { toast.error("Masukkan harga lebih dari 0"); return; }
    const tiers = priceProd.price_tiers || [];
    const newTiers = tiers.length
      ? tiers.map((t, i) => (i === 0 ? { name: t.name || "Umum", min_qty: t.min_qty || 1, price: val } : t))
      : [{ name: "Umum", min_qty: 1, price: val }];
    try {
      await api.put(`/products/${priceProd.id}`, { price_tiers: newTiers });
      toast.success(`Harga ${priceProd.name} disimpan`);
      setPriceProd(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const onVariantKey = (e) => {
    if (!quick) return;
    const vs = quick.product.variants || [];
    if (!vs.length) return;
    if (e.target?.dataset?.variantDecrement) return;
    if (e.key === "ArrowDown") { e.preventDefault(); document.querySelector('[data-testid="quick-add-confirm"]')?.focus(); }
    else if (e.key === "ArrowRight") { e.preventDefault(); varRef.current?.focus(); setQuickVarSel((s) => (s + 1) % vs.length); }
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); varRef.current?.focus(); setQuickVarSel((s) => (s - 1 + vs.length) % vs.length); }
    else if (e.key === "Enter" || (e.key === " " && e.target === varRef.current)) { e.preventDefault(); if (!e.repeat) changeQuickVariant(quickVarSel); }
  };

  const onUnitsKey = (e) => {
    if (!quick) return;
    const total = quickOpts.length;
    if (e.key === "ArrowDown") { e.preventDefault(); setQuickSel((s) => (s + 1) % total); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setQuickSel((s) => (s - 1 + total) % total); }
    else if (e.key === "Enter") { e.preventDefault(); gotoQty(); }
  };

  const confirmQuick = () => {
    if (!quick) return;
    const unit = quickOpts[quickSel] || null;
    const p = quick.product;
    const qn = quickHasSelectedVariants ? quickTotalQty : Number(quickQty) > 0 ? Number(quickQty) : 1;
    if (!(qn > 0)) { toast.error("Pilih jumlah varian yang akan diambil"); return; }
    const eff = (quickPrice !== "" ? (Number(quickPrice) || 0) : quickUnitPrice(unit, qn)) - (Number(quickDiscount) || 0) / qn;
    const unitCost = (p.cost || 0) * (unit ? Number(unit.pieces || 1) : 1);
    if ((p.cost || 0) > 0 && eff < unitCost - 0.001) {
      toast.error(`Harga jual di bawah harga beli (modal ${rupiah(unitCost)})`);
      return;
    }
    addToCart(p, unit, qn, quickPrice, null, quickDiscount, quickNote, quickVariantQuantities);
    setQuick(null);
    setTimeout(() => { searchRef.current?.focus(); searchRef.current?.select(); }, 40);
  };

  const closeQuick = () => { setQuick(null); setTimeout(() => { searchRef.current?.focus(); searchRef.current?.select(); }, 40); };

  // Navigasi keyboard di grid produk
  const colsOf = () => {
    const el = gridRef.current;
    if (!el) return 1;
    const cards = el.querySelectorAll('[data-testid^="product-card-"]');
    if (cards.length < 2) return 1;
    const top0 = cards[0].offsetTop;
    let c = 0;
    for (const cd of cards) { if (cd.offsetTop === top0) c++; else break; }
    return Math.max(1, c);
  };
  const onGridKey = (e) => {
    if (!filtered.length) return;
    const cols = colsOf();
    let idx = hi < 0 ? 0 : hi;
    if (e.key === "ArrowRight") { e.preventDefault(); idx = Math.min(filtered.length - 1, idx + 1); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); idx = Math.max(0, idx - 1); }
    else if (e.key === "ArrowDown") { e.preventDefault(); idx = Math.min(filtered.length - 1, idx + cols); }
    else if (e.key === "ArrowUp") { e.preventDefault(); if (idx - cols < 0) { setHi(-1); searchRef.current?.focus(); searchRef.current?.select(); return; } idx = idx - cols; }
    else if (e.key === "Enter") { e.preventDefault(); const p = filtered[idx]; if (p) openQuick(p); return; }
    else return;
    setHi(idx);
  };

  useEffect(() => { setHi(-1); }, [search, activeCat]);
  useEffect(() => {
    if (hi < 0 || !gridRef.current) return;
    const cards = gridRef.current.querySelectorAll('[data-testid^="product-card-"]');
    cards[hi]?.scrollIntoView({ block: "nearest" });
  }, [hi]);

  const changeQty = (i, delta) => {
    const variants = itemVariantQuantities(cart[i]);
    if (variants.length > 1) { setExpandedCart(i); toast.info("Ubah jumlah pada varian yang diambil"); return; }
    setCart((prev) =>
      prev
        .map((it, idx) => (idx === i ? variants.length ? changeVariantQuantity(it, variants[0].name, delta) : { ...it, qty: it.qty + delta } : it))
        .filter((it) => it.qty > 0)
    );
  };
  const changeCartVariant = (i, name, delta) => setCart((prev) => prev.map((it, idx) => idx === i ? changeVariantQuantity(it, name, delta) : it).filter((it) => it.qty > 0));

  const removeItem = (i) => setCart((prev) => prev.filter((_, idx) => idx !== i));

  const onCartKey = (e) => {
    if (!cart.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setCartHi((h) => Math.min(cart.length - 1, (h < 0 ? -1 : h) + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCartHi((h) => Math.max(0, (h < 0 ? 0 : h) - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); if (cartHi >= 0) setExpandedCart((o) => (o === cartHi ? null : cartHi)); }
    else if (e.key === "+" || e.key === "=") { e.preventDefault(); if (cartHi >= 0) changeQty(cartHi, 1); }
    else if (e.key === "-") { e.preventDefault(); if (cartHi >= 0) changeQty(cartHi, -1); }
    else if (e.key === "Delete") { e.preventDefault(); if (cartHi >= 0) { removeItem(cartHi); setExpandedCart(null); setCartHi((h) => Math.min(h, cart.length - 2)); } }
  };

  useEffect(() => {
    if (cartHi < 0) return;
    if (cartHi > cart.length - 1) { setCartHi(cart.length - 1); return; }
    const row = cartRef.current?.querySelector(`[data-testid="cart-row-${cartHi}"]`);
    row?.scrollIntoView({ block: "nearest" });
  }, [cartHi, cart.length]);

  const setItemDiscount = (i, v) => setCart((prev) => prev.map((it, x) => (x === i ? { ...it, discount: Math.max(0, Number(v) || 0) } : it)));
  const setItemPrice = (i, v) => setCart((prev) => prev.map((it, x) => (x === i ? { ...it, manualPrice: (v === "" || v == null) ? null : Math.max(0, Number(v) || 0) } : it)));
  const setItemNote = (i, v) => setCart((prev) => prev.map((it, x) => (x === i ? { ...it, note: v } : it)));
  const clearCart = () => {
    setCart([]);
    setDiscount(0);
    setCustomer(null);
    setReceiptNote("");
  };

  const doHold = async (label) => {
    if (cart.length === 0 && !previousDebts.length) return;
    try {
      await api.post("/holds", { label: (label || "").trim(), payload: { items: cart, discount, customer, note: receiptNote, previousDebts } });
      setCart([]);
      setDiscount(0);
      setCustomer(null);
      setReceiptNote("");
      setHoldOpen(false);
      setHoldLabel("");
      loadHolds();
      toast.success("Keranjang ditahan (tampil di semua kasir toko ini)");
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const recallCart = async (id) => {
    const found = holds.find((x) => x.id === id);
    if (!found) return;
    try {
      await api.delete(`/holds/${id}`);
      if (cart.length > 0 || previousDebts.length) await api.post("/holds", { label: customer?.name || "", payload: { items: cart, discount, customer, note: receiptNote, previousDebts } });
      setCart(found.payload?.items || []);
      setDiscount(found.payload?.discount || 0);
      setCustomer(found.payload?.customer || null);
      setReceiptNote(found.payload?.note || "");
      setPosDraft(previous => ({ ...previous, previousDebts: found.payload?.previousDebts || [] }));
      loadHolds();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const removeQueue = async (id) => {
    try { await api.delete(`/holds/${id}`); loadHolds(); } catch (e) { toast.error(apiError(e)); }
  };

  const lineTotal = (it) => Math.max(0, it.qty * priceOf(it) - Number(it.discount || 0));
  const subtotal = cart.reduce((s, it) => s + lineTotal(it), 0);
  const saleTotal = Math.max(0, subtotal - Number(discount || 0));
  const settlementTotal = previousDebts.reduce((sum, debt) => sum + debt.amount, 0);
  const total = saleTotal + settlementTotal;
  const hasBelowCostItems = cart.some(isBelowCost);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "F2") {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === "F8" && (cart.length || settlementTotal) && !debtCustomer && !payBusy) {
        e.preventDefault();
        if (!shift) { toast.error("Buka shift dulu sebelum transaksi"); return; }
        if (hasBelowCostItems) { toast.error("Perbaiki harga produk bertanda merah sebelum pembayaran"); return; }
        setPayOpen(true);
      }
      if (e.key === "F9" && (cart.length || settlementTotal)) {
        e.preventDefault();
        cetakResi();
      }
      if (e.key === "F10" && cart.length) {
        e.preventDefault();
        cartRef.current?.focus();
        setCartHi((h) => (h < 0 ? 0 : h));
      }
      if (e.key === "F3") {
        e.preventDefault();
        setCustOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cart, shift, hasBelowCostItems, discount, receiptNote, customer, settlementTotal, debtCustomer, payBusy]);

  const cetakResi = async () => {
    if (readOnly) return;
    if (cart.length === 0 && !settlementTotal) return;
    const draft = {
      invoice_no: "DRAFT",
      created_at: new Date().toISOString(),
      cashier: user.name,
      items: cart.map((it) => ({ name: it.name, qty: it.qty, price: priceOf(it), discount: Number(it.discount || 0), variant_quantities: itemVariantQuantities(it), note: it.note, unit: labelOf(it), price_level: labelOf(it), pieces: piecesOf(it) })),
      subtotal, discount: Number(discount || 0), total: saleTotal, previous_debts: previousDebts, settlement_total: settlementTotal,
      payments: [], paid: 0, change: 0, fee: 0, note: receiptNote, customer_name: customer?.name,
    };
    try {
      const { data: s } = await api.get("/settings");
      const mode = printerMode(s);
      if (mode === "ethernet") {
        if (!s.eth_ip) { toast.error("Isi alamat IP printer di Pengaturan Printer terlebih dahulu"); return false; }
        await api.post("/print/draft", draft);
        toast.success("Resi tercetak");
      } else if (mode === "bluetooth") {
        await printViaBluetooth(draft, s, user.name);
        toast.success("Resi tercetak");
      } else {
        if (!printReceipt(draft, s, user.name)) { toast.error("Izinkan popup browser untuk mencetak resi"); return false; }
      }
      return true;
    } catch (e) { toast.error(apiError(e)); return false; }
  };

  const onPaid = async (payload) => {
    if (readOnly || paying.current) return;
    if (!shift) { toast.error("Buka shift dulu sebelum transaksi"); return; }
    // Blokir harga jual di bawah harga beli (termasuk setelah diskon item)
    for (const it of cart) {
      const unitCost = (it.cost || 0) * piecesOf(it);
      if (isBelowCost(it)) {
        toast.error(`Harga jual "${it.name}" di bawah harga beli (modal ${rupiah(unitCost)})`);
        return;
      }
    }
    const body = {
      previous_debts: previousDebts.map(({ invoice_id, amount }) => ({ invoice_id, amount })),
      items: cart.map((it) => ({
        product_id: it.product_id, name: it.name, qty: it.qty, variant_quantities: itemVariantQuantities(it),
        price: priceOf(it), price_level: labelOf(it),
        unit: labelOf(it), pieces: piecesOf(it),
        discount: Number(it.discount || 0), note: (it.note || "").trim() || null,
      })),
      discount: Number(discount || 0),
      ...payload,
      note: receiptNote.trim() || null,
    };
    const signature = JSON.stringify(body);
    if (paymentAttempt.current?.signature !== signature) paymentAttempt.current = { signature, id: globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}` };
    body.client_request_id = paymentAttempt.current.id;
    if (settlementTotal && !navigator.onLine) { toast.error("Pelunasan piutang sebelumnya memerlukan koneksi internet"); return; }
    paying.current = true; setPayBusy(true);
    if (navigator.onLine) {
      try {
        const { data } = await api.post("/transactions", body);
        setPayOpen(false);
        setReceipt(data);
        clearCart();
        paymentAttempt.current = null;
        load();
        toast.success(`Transaksi ${data.invoice_no} berhasil`);
        return;
      } catch (e) {
        if (e.response) { toast.error(apiError(e)); return; } // server menolak (validasi) — jangan antre
        if (settlementTotal) { toast.error("Koneksi terputus. Coba pembayaran lagi untuk memeriksa hasil; piutang tidak dicatat lunas secara offline."); return; }
        // tidak ada respons = masalah jaringan → simpan offline
      } finally { paying.current = false; setPayBusy(false); }
    }
    // Offline: simpan ke antrian + kurangi stok lokal (perkiraan)
    const localTx = buildLocalTx(body, user.name);
    enqueueTx(body);
    setProducts((prev) => {
      const dec = {};
      cart.forEach((it) => { dec[it.product_id] = (dec[it.product_id] || 0) + it.qty * (it.unit?.pieces || 1); });
      const next = prev.map((p) => (dec[p.id] ? { ...p, stock: p.stock - dec[p.id] } : p));
      cacheProductsOnly(next);
      return next;
    });
    setPayOpen(false);
    setReceipt(localTx);
    clearCart();
    setPending(getQueue().length);
    paying.current = false; setPayBusy(false); paymentAttempt.current = null;
    setOnline(false);
    toast.warning(`Offline: transaksi disimpan (${localTx.invoice_no}) & akan disinkron otomatis`);
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name;

  return (
    <div className="h-full flex flex-col lg:flex-row overflow-hidden">
      {/* Catalog */}
      <div className="flex-1 flex flex-col overflow-hidden p-4 gap-4 bg-ui-canvas">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Button
              data-testid="toggle-sidebar-btn"
              size="sm"
              variant="outline"
              onClick={() => setNavHidden((v) => !v)}
              title={navHidden ? "Tampilkan menu" : "Sembunyikan menu"}
              className="h-8 border-ui-line bg-ui-card hover:bg-ui-card gap-1 text-xs"
            >
              {navHidden ? <PanelLeftOpen className="h-3.5 w-3.5" /> : <PanelLeftClose className="h-3.5 w-3.5" />}
              <span className="hidden sm:inline">{navHidden ? "Menu" : "Sembunyikan"}</span>
            </Button>
            <div data-testid="net-status" className={`flex items-center gap-1.5 text-xs font-bold px-2.5 py-1.5 rounded-lg border ${online ? "bg-ui-card/15 text-ui-text border-ui-line/30" : "bg-danger text-ui-text border-danger"}`}>
              {online ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
              {online ? "Online" : "Offline"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {shiftLoaded && (shift ? (
              <div className="flex items-center gap-1.5">
                <span data-testid="shift-active-badge" className="text-xs font-semibold text-ui-text bg-ui-card/15 border border-ui-line/30 rounded-lg px-2 py-1.5">
                  Shift aktif · {shift.opened_at ? new Date(shift.opened_at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }) : ""}
                </span>
                <WriteButton
                  data-testid="close-shift-btn"
                  size="sm"
                  variant="outline"
                  onClick={() => setShiftCloseOpen(true)}
                  className="h-8 border-danger/40 bg-danger-soft text-danger-readable hover:bg-danger-soft gap-1 text-xs"
                >
                  <Clock className="h-3.5 w-3.5" /> Tutup Shift
                </WriteButton>
              </div>
            ) : (
              <WriteButton
                data-testid="open-shift-btn"
                size="sm"
                onClick={openShift}
                className="h-8 bg-brand-button hover:bg-brand-button text-ui-text font-bold gap-1 text-xs"
              >
                <Clock className="h-3.5 w-3.5" /> Buka Shift
              </WriteButton>
            ))}
            {pending > 0 && (
              <span data-testid="pending-count" className="text-xs font-semibold text-warning-readable bg-warning-soft border border-warning/30 rounded-lg px-2 py-1">
                {pending} menunggu sync
              </span>
            )}
            <Button
              data-testid="touch-mode-btn"
              size="sm"
              variant="outline"
              onClick={toggleTouch}
              title={touchMode ? "Mode Sentuh (tanpa shortcut)" : "Mode Keyboard (tampil shortcut)"}
              className="h-8 border-ui-line bg-ui-card hover:bg-ui-card gap-1 text-xs"
            >
              {touchMode ? <Hand className="h-3.5 w-3.5" /> : <Keyboard className="h-3.5 w-3.5" />}
              <span className="hidden sm:inline">{touchMode ? "Sentuh" : "Keyboard"}</span>
            </Button>
            <Button
              data-testid="fullscreen-btn"
              size="sm"
              variant="outline"
              onClick={toggleFull}
              title={fs ? "Buka kunci layar penuh" : "Kunci layar penuh (sembunyikan bar browser)"}
              className={`h-8 gap-1 text-xs ${fs ? "border-brand bg-brand-soft text-brand-readable" : "border-ui-line bg-ui-card hover:bg-ui-card"}`}
            >
              {fs ? <LockOpen className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
              <span className="hidden sm:inline">{fs ? "Buka Kunci" : "Kunci Layar"}</span>
            </Button>
            {installEvt && (
              <Button
                data-testid="install-app-btn"
                size="sm"
                onClick={doInstall}
                title="Pasang sebagai aplikasi (tanpa bar browser, bisa untuk split screen)"
                className="h-8 bg-brand-button hover:bg-brand-button text-ui-text font-bold gap-1 text-xs"
              >
                <Download className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Pasang Aplikasi</span>
              </Button>
            )}
            <WriteButton
              data-testid="sync-now-btn"
              size="sm"
              variant="outline"
              onClick={doSync}
              disabled={!online || pending === 0}
              className="h-8 border-ui-line bg-ui-card hover:bg-ui-card gap-1 text-xs disabled:opacity-40"
            >
              <RefreshCw className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Sync sekarang</span>
            </WriteButton>
          </div>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-ui-muted" />
          <Input
            ref={searchRef}
            data-testid="pos-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={onSearchKey}
            placeholder="Cari produk / scan SKU / barcode… (F2, ↓ pilih produk)"
            className="h-12 pl-11 bg-ui-card border-ui-line text-base"
          />
        </div>

        {/* Grid */}
        <div ref={gridRef} tabIndex={0} onKeyDown={onGridKey} className="flex-1 overflow-y-auto outline-none">
          {filtered.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-ui-text/90 gap-3">
              <PackageX className="h-12 w-12" />
              <p>{readOnly ? "Belum ada produk untuk ditampilkan. Pilih tenant melalui bagian atas halaman." : "Tidak ada produk. Tambahkan produk lewat menu Produk & Stok."}</p>
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 2xl:grid-cols-9 gap-2">
              {filtered.map((p, i) => {
                const tiers = p.price_tiers || [];
                const cardPrice = baseSellable(p)
                  ? (tiers[autoTierIndex(tiers, 1)]?.price || 0)
                  : ((p.units || [])[0]?.price || tiers[0]?.price || 0);
                const price = cardPrice;
                const multiTier = tiers.length > 1;
                const baseNoPrice = !tiers.some((t) => Number(t.price) > 0) && !(p.units || []).some((u) => Number(u.price) > 0);
                const out = p.stock <= 0;
                const active = i === hi;
                return (
                  <div
                    key={p.id}
                    data-testid={`product-card-${p.id}`}
                    onClick={() => openQuick(p)}
                    className={`text-left rounded-xl border p-2 transition-all no-select cursor-pointer active:scale-[0.97] ${
                      active
                        ? "border-warning bg-warning-soft ring-4 ring-warning shadow-lg shadow-amber-500/30 scale-[1.02]"
                        : out
                        ? "border-warning/40 bg-ui-soft hover:border-warning/70"
                        : "border-ui-line bg-ui-card hover:border-brand/60 hover:bg-ui-card"
                    }`}
                  >
                    <div className="aspect-square rounded-lg bg-ui-soft mb-1.5 overflow-hidden flex items-center justify-center relative">
                      {p.image ? (
                        <ApiImage src={imgUrl(p.image)} alt={p.name} className="w-full h-full object-cover" />
                      ) : (
                        <ShoppingCart className="h-8 w-8 text-ui-muted" />
                      )}
                      {p.note && (
                        <span data-testid={`card-note-${p.id}`} title={p.note} className="absolute top-1 right-1 h-5 w-5 rounded-md bg-warning/90 flex items-center justify-center">
                          <StickyNote className="h-3 w-3 text-ui-text" />
                        </span>
                      )}
                      {baseNoPrice && (
                        <button
                          data-testid={`card-noprice-${p.id}`}
                          onClick={(e) => { e.stopPropagation(); openPrice(p); }}
                          title="Harga belum diisi — klik untuk set harga jual"
                          className="absolute top-1 left-1 h-5 w-5 rounded-md bg-danger/90 flex items-center justify-center hover:bg-danger cursor-pointer"
                        >
                          <AlertTriangle className="h-3 w-3 text-ui-text" />
                        </button>
                      )}
                    </div>
                    <div className="text-xs font-medium leading-tight line-clamp-2 min-h-[2rem]">{p.name}</div>
                    {p.note && <div className="text-[10px] text-warning-readable/90 leading-tight line-clamp-1 mt-0.5">{p.note}</div>}
                    <div className="mt-0.5 flex items-center justify-between gap-1">
                      <span className={`font-mono font-bold text-xs ${baseNoPrice ? "text-danger-readable" : "text-brand-readable"}`}>{baseNoPrice ? "Belum ada harga" : rupiah(price)}</span>
                      <span className={`text-[10px] shrink-0 font-semibold ${p.stock <= 0 ? "text-danger-readable" : p.stock <= p.min_stock ? "text-warning-readable" : "text-ui-muted"}`}>
                        {p.stock === 0 ? "Habis" : p.stock}
                      </span>
                    </div>
                    {p.units && p.units.length > 0 ? (
                      <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`card-units-${p.id}`}>
                        {baseSellable(p) && (
                          <WriteButton plain
                            data-testid={`add-unit-base-${p.id}`}
                            onClick={(e) => { e.stopPropagation(); openQuick(p, null); }}
                            className="px-2 h-6 rounded-md text-[10px] font-semibold bg-brand-soft text-brand-readable border border-brand/40 no-select"
                          >
                            + {p.base_unit || "Pcs"}
                          </WriteButton>
                        )}
                        {p.units.map((u, ui) => (
                          <WriteButton plain
                            key={ui}
                            data-testid={`add-unit-${p.id}-${ui}`}
                            onClick={(e) => { e.stopPropagation(); openQuick(p, u); }}
                            className="px-2 h-6 rounded-md text-[10px] font-semibold bg-accent-soft text-accent-readable border border-accent/40 no-select"
                          >
                            + {u.name}
                          </WriteButton>
                        ))}
                      </div>
                    ) : (
                      multiTier && <div className="text-[9px] text-warning-readable/90 mt-0.5">{tiers.length} tingkat harga</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Category tabs (bawah) */}
        <div className="flex gap-2 overflow-x-auto pt-1 shrink-0 border-t border-ui-line">
          <CatChip active={activeCat === "all"} onClick={() => setActiveCat("all")} label="Semua" />
          {categories.map((c) => (
            <CatChip
              key={c.id}
              active={activeCat === c.id}
              onClick={() => setActiveCat(c.id)}
              label={`${c.name} (${c.product_count})`}
            />
          ))}
        </div>
      </div>

      {/* Cart */}
      <div className="w-full lg:w-[380px] xl:w-[420px] bg-ui-card border-t lg:border-t-0 lg:border-l border-brand flex flex-col shrink-0 max-h-[45vh] lg:max-h-none">
        <div className="h-16 px-4 flex items-center justify-between border-b border-brand/60">
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-ui-text" />
            <span className="font-heading font-bold text-ui-text">Keranjang</span>
            <span className="text-xs bg-ui-card/20 px-2 py-0.5 rounded-full text-ui-text">{cart.length}</span>
            {holds.length > 0 && (
              <div className="relative">
                <button
                  data-testid="queue-toggle-btn"
                  onClick={() => setQueueOpen((v) => !v)}
                  className="text-xs bg-warning text-warning-readable font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 no-select hover:bg-warning shadow-sm"
                >
                  <Pause className="h-3 w-3" />
                  <span data-testid="queue-count">{holds.length}</span> antrian
                </button>
                {queueOpen && (
                  <div data-testid="queue-list" className="absolute z-30 right-0 top-full mt-1 w-72 max-w-[calc(100vw-2rem)] rounded-lg border border-ui-line bg-ui-card shadow-xl max-h-72 overflow-y-auto">
                    <div className="divide-y divide-ui-line">
                    {holds.map((q, qi) => (
                      <WriteButton plain
                        key={q.id}
                        data-testid={`recall-queue-${qi}`}
                        onClick={() => { recallCart(q.id); setQueueOpen(false); }}
                        className="w-full text-left px-3 py-2 no-select hover:bg-ui-muted-surface/60"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-semibold text-warning-readable truncate min-w-0">{q.label || `Antrian ${qi + 1}`}</span>
                          <span data-testid={`queue-total-${qi}`} className="text-xs font-bold text-brand-readable shrink-0 whitespace-nowrap">{rupiah(holdTotal(q))}</span>
                        </div>
                        <div className="text-[11px] text-ui-muted mt-0.5 truncate">
                          {(q.payload?.items || []).reduce((s, it) => s + (Number(it.qty) || 0), 0)} item{q.by ? ` · ${q.by}` : ""}
                        </div>
                      </WriteButton>
                    ))}
                    </div>
                    <div data-testid="queue-grand-total" className="sticky bottom-0 flex items-center justify-between gap-2 border-t border-ui-line bg-ui-soft px-3 py-2">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-ui-muted whitespace-nowrap">Total {holds.length} Antrian</span>
                      <span className="text-sm font-extrabold text-brand-readable shrink-0 whitespace-nowrap">{rupiah(holds.reduce((s, q) => s + holdTotal(q), 0))}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          {(cart.length > 0 || previousDebts.length > 0) && (
            <div className="flex items-center gap-1">
              <WriteButton
                data-testid="hold-cart-btn"
                variant="ghost"
                size="sm"
                onClick={() => { if (!customer) { toast.error("Pilih pelanggan (F3) dulu untuk menamai antrian"); setCustOpen(true); return; } doHold(customer.name); }}
                className="text-ui-text/80 hover:text-warning-readable gap-1"
              >
                <Pause className="h-4 w-4" /> Tahan
              </WriteButton>
              <Button
                data-testid="clear-cart-btn"
                variant="ghost"
                size="sm"
                onClick={() => setResetOpen(true)}
                className="text-ui-text/80 hover:text-danger-readable gap-1"
              >
                <RotateCcw className="h-4 w-4" /> Reset
              </Button>
            </div>
          )}
        </div>

        <div className="px-3 pt-3 shrink-0">
          <button
            data-testid="pos-customer-btn"
            onClick={() => setCustOpen(true)}
            className="w-full flex items-center gap-2 rounded-lg border border-ui-line bg-ui-soft px-3 py-2 text-left hover:border-brand/60 no-select"
          >
            {customer ? <User className="h-4 w-4 text-brand-readable" /> : <UserPlus className="h-4 w-4 text-ui-muted" />}
            <span className="text-xs flex-1 truncate">
              {customer ? <span className="text-ui-text font-semibold" data-testid="pos-customer-name">{customer.name}</span> : <span className="text-ui-muted">Pelanggan (F3)</span>}
            </span>
            {customer && (
              <span data-testid="pos-customer-clear" onClick={(e) => { e.stopPropagation(); setCustomer(null); }} className="text-ui-muted hover:text-danger-readable">
                <X className="h-3.5 w-3.5" />
              </span>
            )}
          </button>
        </div>

        <div ref={cartRef} tabIndex={0} onKeyDown={onCartKey} className="flex-1 overflow-y-auto p-2 space-y-1.5 outline-none focus:ring-2 focus:ring-ui-line/40 rounded-lg" data-testid="cart-items">
          {cart.length === 0 && !settlementTotal ? (
            <div className="h-full flex flex-col items-center justify-center text-ui-text/70 gap-2 py-8">
              <ShoppingCart className="h-10 w-10" />
              <p className="text-sm">Keranjang kosong</p>
            </div>
          ) : (
            cart.map((it, i) => {
              const price = priceOf(it);
              const open = expandedCart === i;
              const unitCost = (it.cost || 0) * piecesOf(it);
              const belowModal = isBelowCost(it);
              return (
              <div
                key={i}
                data-testid={`cart-row-${i}`}
                onClick={() => { setCartHi(i); setExpandedCart(open ? null : i); }}
                className={`rounded-lg border animate-pop cursor-pointer transition-colors ${i === cartHi ? "ring-2 ring-warning " : ""}${belowModal ? "bg-danger-soft border-danger" : open ? "bg-ui-card border-brand/50" : "bg-ui-soft border-ui-line hover:border-ui-line"}`}
              >
                <div className="flex items-center gap-2 px-2.5 py-1.5">
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium leading-tight truncate">{it.name}</div>
                    <div className="text-[10px] text-ui-muted leading-tight">
                      {it.unit ? `${it.unit.name} · ` : ""}{rupiah(price)}{it.unit ? ` ×${it.unit.pieces}pcs` : "/pcs"}
                      {it.discount > 0 ? <span className="text-danger-readable"> · -{rupiah(it.discount)}</span> : null}
                      {it.note ? <span className="text-warning-readable"> · {it.note}</span> : null}
                    </div>
                    {variantSummary(it) && <div data-testid={`cart-variants-${i}`} className="text-xs text-accent-readable mt-1">{variantSummary(it)}</div>}
                  </div>
                  <span className="shrink-0 text-xs font-mono font-bold bg-ui-soft border border-ui-line rounded px-1.5 py-0.5">×{it.qty}</span>
                  <span className={`shrink-0 w-20 text-right font-mono font-bold text-sm ${belowModal ? "text-danger-readable" : "text-brand-readable"}`}>{rupiah(lineTotal(it))}</span>
                </div>
                {belowModal && (
                  <div data-testid={`cart-price-warning-${i}`} className="flex items-center gap-1 px-2.5 pb-2 text-[11px] font-semibold text-danger-readable">
                    <AlertTriangle className="h-3 w-3 shrink-0" /> Harga di bawah modal ({rupiah(unitCost)}). Perbaiki harga sebelum bayar.
                  </div>
                )}
                {open && (
                  <div className="px-2.5 pb-2 pt-0.5 space-y-2" onClick={(e) => e.stopPropagation()}>
                    {itemVariantQuantities(it).map((v, vi) => <div key={v.name} className="flex items-center justify-between text-xs" onKeyDown={(e) => e.stopPropagation()}>
                      <span>{v.name}</span>
                      <div className="flex items-center gap-2">
                        <button aria-label={`Kurangi ${v.name}`} data-testid={`cart-variant-minus-${i}-${vi}`} className="h-8 w-8 rounded border border-ui-line" onClick={() => changeCartVariant(i, v.name, -1)}>−</button>
                        <span className="font-mono">{v.qty}</span>
                        <button aria-label={`Tambah ${v.name}`} data-testid={`cart-variant-plus-${i}-${vi}`} className="h-8 w-8 rounded border border-ui-line" onClick={() => changeCartVariant(i, v.name, 1)}>+</button>
                      </div>
                    </div>)}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button
                          data-testid={`cart-minus-${i}`}
                          onClick={() => changeQty(i, -1)}
                          className="h-8 w-8 rounded-lg bg-ui-soft border border-ui-line flex items-center justify-center hover:border-brand no-select"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <span className="w-8 text-center font-mono font-bold">{it.qty}</span>
                        <button
                          data-testid={`cart-plus-${i}`}
                          onClick={() => changeQty(i, 1)}
                          className="h-8 w-8 rounded-lg bg-ui-soft border border-ui-line flex items-center justify-center hover:border-brand no-select"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                      <button
                        data-testid={`cart-remove-${i}`}
                        onClick={() => { removeItem(i); setExpandedCart(null); }}
                        className="h-8 px-2 rounded-lg bg-ui-soft border border-ui-line hover:bg-danger-soft hover:border-danger/50 flex items-center gap-1 text-xs text-danger-readable no-select"
                      >
                        <Trash2 className="h-4 w-4" /> Hapus
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-ui-muted w-14 shrink-0">Harga</span>
                      <MoneyInput
                        data-testid={`cart-price-${i}`}
                        value={priceOf(it) || ""}
                        onValueChange={(v) => setItemPrice(i, v)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            if (belowModal) { toast.error(`Harga di bawah modal (${rupiah(unitCost)})`); return; }
                            setExpandedCart(null);
                          }
                        }}
                        placeholder="0"
                        className={`h-8 text-right font-mono bg-ui-soft text-xs ${belowModal ? "border-danger ring-1 ring-danger" : "border-ui-line"}`}
                      />
                      {it.manualPrice != null && (
                        <button
                          data-testid={`cart-price-reset-${i}`}
                          onClick={() => setItemPrice(i, "")}
                          title="Kembalikan ke harga otomatis"
                          className="h-8 px-2 rounded-lg bg-ui-soft border border-ui-line text-ui-muted hover:text-brand-readable no-select shrink-0"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-ui-muted w-14 shrink-0">Diskon</span>
                      <MoneyInput
                        data-testid={`cart-discount-${i}`}
                        value={it.discount || ""}
                        onValueChange={(v) => setItemDiscount(i, v)}
                        placeholder="0"
                        className="h-8 text-right font-mono bg-ui-soft border-ui-line text-xs"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-ui-muted w-14 shrink-0">Catatan</span>
                      <Input
                        data-testid={`cart-note-${i}`}
                        value={it.note || ""}
                        onChange={(e) => setItemNote(i, e.target.value)}
                        placeholder="mis. tanpa es / bungkus"
                        className="h-8 bg-ui-soft border-ui-line text-xs"
                      />
                    </div>
                  </div>
                )}
              </div>
              );
            })
          )}
          {previousDebts.length > 0 && <div data-testid="cart-previous-debts" className="border-t border-ui-line mt-3 pt-3 space-y-2">
            {previousDebts.map(debt => <div key={debt.invoice_id} className="flex justify-between gap-2 rounded-lg bg-warning-soft p-3 text-sm">
              <span title={debt.invoice_no}>Piutang sebelumnya ({debt.invoice_no.slice(-4)})</span><strong className="font-mono whitespace-nowrap">{rupiah(debt.amount)}</strong>
            </div>)}
            <button data-testid="cart-remove-previous-debts" className="text-xs text-danger-readable underline" onClick={() => setPosDraft(previous => ({ ...previous, previousDebts: [] }))}>Keluarkan piutang dari keranjang</button>
          </div>}
        </div>

        <div className="border-t border-brand/60 p-4 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-ui-text/80">Subtotal</span>
            <span className="font-mono text-ui-text">{rupiah(subtotal)}</span>
          </div>
          {discount > 0 && <div data-testid="cart-discount-summary" className="flex justify-between text-sm"><span>Diskon</span><span>-{rupiah(discount)}</span></div>}
          {settlementTotal > 0 && <div className="flex justify-between text-sm"><span>Piutang sebelumnya</span><span>{rupiah(settlementTotal)}</span></div>}
          {receiptNote && <p data-testid="cart-note-summary" className="text-xs text-ui-muted whitespace-pre-wrap break-words line-clamp-2"><StickyNote className="inline h-3 w-3 mr-1" />{receiptNote}</p>}
          <div className="flex items-center justify-between pt-2 border-t border-brand/60">
            <span className="font-heading font-bold text-lg text-ui-text">Total</span>
            <span data-testid="cart-total" className="font-mono font-extrabold text-2xl text-ui-text">
              {rupiah(total)}
            </span>
          </div>
          {hasBelowCostItems && (
            <p data-testid="checkout-price-warning" role="alert" className="rounded-lg border border-danger bg-danger-soft p-2 text-xs font-semibold text-danger-readable">
              Pembayaran diblokir. Perbaiki harga atau hapus produk bertanda merah dari keranjang.
            </p>
          )}
          {shift ? (
            <WriteButton
              data-testid="checkout-button"
              disabled={(!cart.length && !settlementTotal) || hasBelowCostItems || payBusy || !!debtCustomer}
              onClick={() => { if (!hasBelowCostItems) setPayOpen(true); }}
              className="w-full h-14 text-lg font-bold bg-brand-button hover:bg-brand-hover gap-2"
            >
              <CreditCard className="h-5 w-5" /> Bayar (F8)
            </WriteButton>
          ) : (
            <WriteButton
              data-testid="checkout-open-shift-btn"
              onClick={openShift}
              className="w-full h-14 text-lg font-bold bg-warning hover:bg-warning text-ui-text gap-2"
            >
              <Clock className="h-5 w-5" /> Buka Shift untuk Transaksi
            </WriteButton>
          )}
          <CartActions action={cartAction} onAction={setCartAction} readOnly={readOnly} storeName={storeName} phone={customer?.phone}
            tx={{ cashier: user.name, created_at: new Date().toISOString(), items: cart.map(it => ({ product_id: it.product_id, price_level: labelOf(it), name: it.name, qty: it.qty, price: priceOf(it), unit: labelOf(it), discount: Number(it.discount || 0), note: it.note, variant_quantities: itemVariantQuantities(it) })), subtotal, discount: Number(discount || 0), total: saleTotal, previous_debts: previousDebts, settlement_total: settlementTotal, note: receiptNote, customer_name: customer?.name }}
            onDiscount={setDiscount} onNote={setReceiptNote} onPrint={cetakResi} onCloseFocus={() => searchRef.current?.focus()} />
        </div>
      </div>

      <Dialog open={!!quick} onOpenChange={(o) => { if (!o) closeQuick(); }}>
        <DialogContent
          data-testid="quick-add-dialog"
          aria-describedby={undefined}
          onKeyDown={(e) => {
            if (e.key === "Control" && (quick?.product?.variants || []).length) { e.preventDefault(); varRef.current?.focus(); }
            else if (e.key === "Shift" && e.target?.dataset?.testid !== "quick-note-input") { e.preventDefault(); confirmQuick(); }
          }}
          className="bg-ui-card border-ui-line text-ui-text w-[calc(100%-2rem)] max-w-2xl max-h-[92vh] overflow-y-auto"
        >
          {quick && (() => {
            const p = quick.product;
            const units = p.units || [];
            const hasUnits = units.length > 0;
            const unit = quickOpts[quickSel] || null;
            const basePrice = quickUnitPrice(unit, quickTotalQty);
            const price = quickPrice !== "" ? (Number(quickPrice) || 0) : basePrice;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading text-lg leading-tight">{p.name}</DialogTitle>
                </DialogHeader>
                {p.note && (
                  <div data-testid="quick-note" className="flex items-start gap-1.5 text-xs text-warning-readable bg-warning-soft border border-warning/30 rounded-lg px-2.5 py-1.5">
                    <StickyNote className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span className="leading-snug">{p.note}</span>
                  </div>
                )}
                {(p.variants || []).length > 0 && (
                  <div className="space-y-1.5">
                    <label className="text-xs text-ui-muted">Varian (opsional) — Ctrl untuk fokus, ←/→ pilih, Enter tambah, ↓ ke keranjang</label>
                    <div
                      ref={varRef}
                      tabIndex={0}
                      onKeyDown={onVariantKey}
                      onFocusCapture={() => setQuickVariantFocused(true)}
                      onBlurCapture={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setQuickVariantFocused(false); }}
                      data-testid="quick-variant-options"
                      className="flex flex-wrap gap-2 outline-none focus:ring-1 focus:ring-accent/50 rounded-lg p-0.5"
                    >
                      {p.variants.map((v, vi) => {
                        const sel = vi === quickVarSel && quickVariantFocused;
                        return (
                          <div key={vi} className="flex items-center gap-1">
                          <button
                            type="button"
                            data-testid={`quick-variant-${vi}`}
                            onFocus={() => setQuickVarSel(vi)}
                            onClick={() => { changeQuickVariant(vi); varRef.current?.focus(); }}
                            className={`px-3 h-10 rounded-lg text-sm font-semibold no-select border ${sel ? "bg-accent text-ui-text border-accent" : "bg-ui-card text-ui-text border-ui-line hover:border-ui-line"}`}
                          >
                            {v.name}
                            {quickVariantCounts[vi] > 0 && <span data-testid={`quick-variant-qty-${vi}`} className="ml-2 rounded bg-ui-card px-1.5 py-0.5 text-xs font-mono text-ui-text">{quickVariantCounts[vi]}</span>}
                          </button>
                          {quickVariantCounts[vi] > 0 && <button type="button" data-variant-decrement="true" aria-label={`Kurangi ${v.name}`} data-testid={`quick-variant-minus-${vi}`} className="h-8 w-7 rounded border border-ui-line" onClick={() => { changeQuickVariant(vi, -1); varRef.current?.focus(); }}>−</button>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {hasUnits && (
                  <div className="space-y-1.5">
                    <label className="text-xs text-ui-muted">Pilih Satuan — panah ↑/↓ lalu Enter</label>
                    <div
                      ref={optsRef}
                      tabIndex={0}
                      onKeyDown={onUnitsKey}
                      data-testid="quick-unit-options"
                      className="outline-none flex flex-col gap-1.5 focus:ring-1 focus:ring-brand/40 rounded-lg"
                    >
                      {quickOpts.map((u, idx) => {
                        const sel = idx === quickSel;
                        return (
                          <div
                            key={idx}
                            data-testid={idx === 0 ? "quick-unit-base" : `quick-unit-${idx - 1}`}
                            onClick={() => { setQuickSel(idx); gotoQty(); }}
                            className={`flex items-center justify-between px-3 h-11 rounded-lg text-sm font-semibold no-select cursor-pointer border ${sel ? "bg-brand-button text-ui-text border-brand" : "bg-ui-card text-ui-text border-ui-line hover:border-ui-line"}`}
                          >
                            <span>{u ? u.name : (p.base_unit || "Pcs")}{u ? ` (isi ${u.pieces} pcs)` : ""}</span>
                            <span className="font-mono">{rupiah(quickUnitPrice(u, quickTotalQty))}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="space-y-1.5">
                  <label className="text-xs text-ui-muted">Jumlah (QTY) — Enter = masuk keranjang</label>
                  <Input
                    ref={qtyRef}
                    data-testid="quick-qty-input"
                    type="text"
                    inputMode="decimal"
                    value={quickHasSelectedVariants ? String(quickTotalQty) : quickQty}
                    readOnly={quickHasSelectedVariants}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => setQuickQty(e.target.value.replace(",", ".").replace(/[^0-9.]/g, ""))}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirmQuick(); } }}
                    className="h-14 text-center text-2xl font-mono font-bold bg-ui-card border-ui-line"
                  />
                  {quickHasSelectedVariants && <p className="text-[11px] text-ui-muted">Total otomatis dari jumlah seluruh varian.</p>}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5">
                    <label className="text-xs text-ui-muted">Diskon (Rp) — Enter</label>
                    <MoneyInput
                      ref={discRef}
                      data-testid="quick-discount-input"
                      value={quickDiscount === "" ? "" : Number(quickDiscount)}
                      placeholder="0"
                      onFocus={(e) => e.target.select()}
                      onValueChange={(v) => setQuickDiscount(v === "" ? "" : String(v))}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); noteRef.current?.focus(); } }}
                      className="h-11 text-right font-mono bg-ui-card border-ui-line"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs text-ui-muted">Catatan — Enter</label>
                    <Input
                      ref={noteRef}
                      data-testid="quick-note-input"
                      value={quickNote}
                      placeholder="mis. tanpa es"
                      onChange={(e) => setQuickNote(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirmQuick(); } }}
                      className="h-11 bg-ui-card border-ui-line text-sm"
                    />
                  </div>
                </div>
                {showPriceEdit ? (
                  <div className="space-y-1.5">
                    <label className="text-xs text-ui-muted">Ubah Harga (per {unit ? unit.name : (p.base_unit || "Pcs")})</label>
                    <MoneyInput
                      data-testid="quick-price-input"
                      value={quickPrice === "" ? "" : Number(quickPrice)}
                      placeholder={basePrice ? basePrice.toLocaleString("id-ID") : "0"}
                      onValueChange={(v) => setQuickPrice(v === "" ? "" : String(v))}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirmQuick(); } }}
                      className="h-11 text-right text-lg font-mono bg-ui-card border-warning/50"
                    />
                    {quickPrice !== "" && (
                      <button type="button" data-testid="quick-price-reset" onClick={() => setQuickPrice("")} className="text-[11px] text-ui-muted hover:text-ui-text">Kembali ke harga default ({rupiah(basePrice)})</button>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    data-testid="quick-price-toggle"
                    onClick={() => setShowPriceEdit(true)}
                    className="text-xs text-warning-readable hover:text-warning-readable font-semibold no-select"
                  >
                    Ubah Harga
                  </button>
                )}
                <div className="flex items-center justify-between text-sm pt-1 border-t border-ui-line">
                  <span className="text-ui-muted">{unit ? unit.name : (p.base_unit || "Pcs")} × {rupiah(price)}{quickPrice !== "" ? " (custom)" : ""}</span>
                  <span data-testid="quick-line-total" className="font-mono font-bold text-brand-readable text-lg">{rupiah(Math.max(0, quickTotalQty * price - (Number(quickDiscount) || 0)))}</span>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={closeQuick} className="border-ui-line bg-transparent hover:bg-ui-card">Batal</Button>
                  <WriteButton data-testid="quick-add-confirm" onClick={confirmQuick} onKeyDown={(e) => { if (e.key === "ArrowUp" && quickHasVariants) { e.preventDefault(); varRef.current?.focus(); } }} className="bg-brand-button hover:bg-brand-button text-ui-text font-bold gap-2">
                    <Plus className="h-4 w-4" /> Masuk Keranjang
                  </WriteButton>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <PaymentModal
        open={payOpen}
        onClose={() => { if (!paying.current) setPayOpen(false); }}
        total={total}
        settlementTotal={settlementTotal}
        busy={payBusy}
        onConfirm={onPaid}
        customer={customer}
        onPickCustomer={() => setCustOpen(true)}
        keyboardMode={!touchMode}
      />
      <CustomerDialog
        open={custOpen}
        onClose={() => setCustOpen(false)}
        selected={customer}
        onSelect={selectCustomer}
      />
      <PreviousDebtDialog customer={debtCustomer} onDone={finishDebtChoice} />
      <ReceiptModal
        tx={receipt}
        cashier={user.name}
        onClose={() => setReceipt(null)}
      />

      <ShiftCloseModal
        open={shiftCloseOpen}
        onClose={() => setShiftCloseOpen(false)}
        storeName={storeName}
        onClosed={() => { setShiftCloseOpen(false); setShift(null); }}
      />

      <Dialog open={!!priceProd} onOpenChange={(o) => { if (!o) setPriceProd(null); }}>
        <DialogContent data-testid="quick-price-dialog" aria-describedby={undefined} className="bg-ui-card border-ui-line text-ui-text max-w-sm max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Set Harga Jual</DialogTitle></DialogHeader>
          {priceProd && (
            <div className="space-y-3">
              <div className="text-sm">
                <div className="font-semibold text-ui-text">{priceProd.name}</div>
                <div className="text-xs text-ui-muted">Modal: {rupiah(priceProd.cost || 0)} / {priceProd.base_unit || "Pcs"}</div>
              </div>
              <div>
                <label className="text-xs text-ui-muted">Harga Jual per {priceProd.base_unit || "Pcs"}</label>
                <MoneyInput
                  data-testid="set-price-input"
                  autoFocus
                  value={priceVal === "" ? "" : Number(priceVal)}
                  onValueChange={(v) => setPriceVal(v === "" ? "" : String(v))}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); savePrice(); } }}
                  placeholder="0"
                  className="mt-1 h-12 text-right font-mono text-lg bg-ui-card border-ui-line"
                />
                {Number(priceVal || 0) > 0 && Number(priceVal) < Number(priceProd.cost || 0) && (
                  <div className="text-[11px] text-danger-readable mt-1">Harga di bawah modal ({rupiah(priceProd.cost || 0)})</div>
                )}
              </div>
              {(priceProd.units || []).length > 0 && (
                <div className="text-[11px] text-ui-muted">Satuan lain (mis. {priceProd.units.map((u) => u.name).join(", ")}) diatur di menu Produk & Stok.</div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPriceProd(null)} className="border-ui-line bg-ui-card hover:bg-ui-muted-surface">Batal</Button>
            <WriteButton data-testid="quick-price-save" onClick={savePrice} className="bg-brand-button hover:bg-brand-button text-ui-text font-bold">Simpan Harga</WriteButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent data-testid="reset-cart-dialog" className="bg-ui-card border-ui-line text-ui-text">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset keranjang?</AlertDialogTitle>
            <AlertDialogDescription className="text-ui-muted">
              Semua item di keranjang{cart.length ? ` (${cart.length} produk)` : ""} akan dihapus dan pelanggan dilepas. Tindakan ini tidak bisa dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-cancel-btn" className="border-ui-line bg-ui-card hover:bg-ui-muted-surface text-ui-text">Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="reset-confirm-btn" onClick={() => { clearCart(); setResetOpen(false); }} className="bg-danger hover:bg-danger text-ui-text">
              Ya, Reset
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CatChip({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`h-9 px-4 rounded-full text-sm font-medium whitespace-nowrap transition-colors no-select ${
        active
          ? "bg-brand-button text-ui-text font-bold"
          : "bg-ui-card text-ui-muted hover:text-ui-text border border-ui-line"
      }`}
    >
      {label}
    </button>
  );
}
